import React, { useEffect } from "react";
import type { FC, ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { authenticated, selectRedirectTo } from "@containers/Auth/store/selectors";
import { setRedirectTo } from "@containers/Auth/store/actions";
import { selectors as SharedSelectors } from "@shared/store";
import { checkIfAdmin } from "@shared/utils/ACL";
import { getFeatures } from "@containers/Account/store/selectors";
import { AuthFlow } from "@shared/interfaces";

import { NameOfChildRoutes, NamesOfParentRoutes, ProductRedirects } from "../constants";

interface GuestGuardProps {
  children: ReactNode;
}

const GuestGuard: FC<GuestGuardProps> = ({ children }) => {
  const currentUser = useSelector(SharedSelectors.getUserDetails());
  const isAuthenticated = useSelector(authenticated());
  let redirectTo = useSelector(selectRedirectTo());
  const features = useSelector(getFeatures());
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const authFlow = searchParams.get("auth_flow") as AuthFlow;

  useEffect(
    () => () => {
      setTimeout(() => {
        dispatch(setRedirectTo(null));
      }, 0);
    },
    [dispatch],
  );

  if (isAuthenticated && currentUser) {
    if (authFlow === AuthFlow.OAuth) {
      return <Navigate to={{ pathname: NamesOfParentRoutes.OAUTH, search: location.search }} />;
    }
    const product = searchParams.get("product") as keyof typeof ProductRedirects;

    if (product) {
      const redirectToProduct = ProductRedirects[product];

      if (redirectToProduct) {
        if (redirectToProduct.feature && !features?.includes(redirectToProduct.feature)) {
          redirectTo = null;
        } else {
          redirectTo = redirectToProduct.url;
        }
      }
    }

    return (
      <Navigate
        to={
          checkIfAdmin(currentUser) ? NameOfChildRoutes.CONTACT.ROOT : redirectTo || NameOfChildRoutes.SKIP_TRACING.ROOT
        }
      />
    );
  }

  return <>{children}</>;
};

export default GuestGuard;
